import {observer} from 'mobx-react-lite'
import React, {useEffect, useLayoutEffect, useRef, useState} from 'react'
import {Lang} from 'yougile-lang'
import {Help} from '@components/common/base/help'
import {WithPerfectScrollbar} from '@components/common/with-scrollbar/with-scrollbar'
import {AnalyticsFunnelChart} from '@components/statistics/crm-analytics/analytics-funnel-chart'
import {CrmAnalyticsStore} from '@components/statistics/crm-analytics/crm-analytics-store'
import {useBoard} from '@containers/hooks'
import {
  TwAnalyticsContainer,
  TwCardBox,
  TwChartContainer,
  TwFunnelRow,
  TwHeader,
  TwHelpContainer,
  TwMain,
  TwNoData,
  TwPercentText,
  TwResultLabel,
  TwTopBlock,
  TwTopTitle,
  TwWipContent,
  TwWipIcon,
  TwWipInner,
  TwWipSubtitle,
  TwWipTitle
} from './components'

export const CrmAnalyticsBase = observer(
  ({crmAnalyticsStore}: {crmAnalyticsStore: CrmAnalyticsStore}) => {
    const board = useBoard()
    const hasData = crmAnalyticsStore.funnelStats.some(stat => stat.count > 0)
    const containerRef = useRef<HTMLDivElement>(null)
    const chartRef = useRef<HTMLDivElement>(null)
    const [inlineBelow, setInlineBelow] = useState(false)

    const cardSizeStyle = chartRef.current
      ? {
          height: (chartRef.current.clientHeight - 16) / 2,
          width: (chartRef.current.clientWidth - 16) / 2
        }
      : {}

    useEffect(() => {
      if (board) {
        crmAnalyticsStore.setBoard(board)
      }
    }, [board])

    useLayoutEffect((): (() => void) | void => {
      const resize = () => {
        const container = containerRef.current
        const chart = chartRef.current
        if (!container || !chart) {
          return
        }

        const containerWidth = container.clientWidth
        const chartWidth = chart.clientWidth
        const cardWidth = (chartWidth - 16) / 2

        setInlineBelow(chartWidth + 16 + cardWidth > containerWidth)
      }

      const containerEl = containerRef.current
      const chartEl = chartRef.current
      if (!containerEl || !chartEl) {
        return
      }

      const observer = new ResizeObserver(resize)
      observer.observe(containerEl)
      observer.observe(chartEl)

      resize()

      return () => observer.disconnect()
    }, [])

    return (
      <TwMain>
        <WithPerfectScrollbar
          scrollDirection="xy"
          className="h-full w-full mr-16"
        >
          <TwAnalyticsContainer>
            <TwFunnelRow ref={containerRef}>
              <TwChartContainer ref={chartRef} className="min-h-[318px]">
                <TwHeader>
                  <TwTopBlock>
                    <TwTopTitle>
                      {Lang.msg.crm.analytics.tableHeader}
                    </TwTopTitle>
                    <TwHelpContainer>
                      <Help name="totalConversion" iconClassName="w-16 h-16" />
                    </TwHelpContainer>
                  </TwTopBlock>
                </TwHeader>

                {hasData ? (
                  <WithPerfectScrollbar
                    scrollDirection="xy"
                    className="h-full w-full"
                  >
                    <AnalyticsFunnelChart
                      stats={crmAnalyticsStore.funnelStats}
                      board={board}
                    />
                  </WithPerfectScrollbar>
                ) : (
                  <TwNoData className="pt-6 px-20">
                    {Lang.msg.crm.analytics.noData}
                  </TwNoData>
                )}
              </TwChartContainer>

              <div
                className={`flex gap-16 ${
                  inlineBelow ? 'flex-row w-full' : 'flex-col'
                }`}
                style={
                  inlineBelow
                    ? {}
                    : {
                        width: chartRef.current
                          ? (chartRef.current.clientWidth - 16) / 2
                          : '300px',
                        height: chartRef.current
                          ? (chartRef.current.clientHeight - 16) / 2
                          : 'auto'
                      }
                }
              >
                <TwCardBox
                  className="min-h-[100px] shrink-0 px-20 py-16 gap-10 p-6"
                  style={cardSizeStyle}
                >
                  <TwTopBlock>
                    <TwResultLabel>
                      {Lang.msg.crm.analytics.successRate}
                    </TwResultLabel>
                    <Help name="conversionSucceed" iconClassName="w-16 h-16" />
                  </TwTopBlock>

                  {hasData ? (
                    <TwPercentText>
                      {crmAnalyticsStore.succeedPercent}%
                    </TwPercentText>
                  ) : (
                    <TwNoData>{Lang.msg.crm.analytics.noData}</TwNoData>
                  )}
                </TwCardBox>

                <TwCardBox
                  className="min-h-[100px] shrink-0 px-20 py-16 gap-10"
                  style={cardSizeStyle}
                >
                  <TwWipContent>
                    <TwWipInner>
                      <TwWipIcon>✦</TwWipIcon>
                      <TwWipTitle>{Lang.msg.crm.analytics.wipTitle}</TwWipTitle>
                      <TwWipSubtitle>
                        {Lang.msg.crm.analytics.wipSubTitle}
                      </TwWipSubtitle>
                    </TwWipInner>
                  </TwWipContent>
                </TwCardBox>
              </div>
            </TwFunnelRow>
          </TwAnalyticsContainer>
        </WithPerfectScrollbar>
      </TwMain>
    )
  }
)
